import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchQuoteById } from '../api/quotesApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Mirrors useCustomer. Subscribes to BOTH UpdateQuoteDetail (the narrow
// per-quote broadcast fired on metadata autosave + line-item CRUD) and
// UpdateQuoteTable (fired on delete / convert) so this page also self-heals
// if another tab wipes the quote out from under us.
//
// `isDirtyRef` is the autosave dirty latch owned by QuoteDetailsCard. While
// dirty, invalidations are stashed into `pendingRefreshRef` and flushed via
// `flushPendingRefresh()` after the save settles — otherwise the SignalR
// echo would race a save and clobber in-flight keystrokes.
export default function useQuote(id, isDirtyRef) {
    const queryClient = useQueryClient();
    const pendingRefreshRef = useRef(false);

    const query = useQuery({
        queryKey: ['quote', id],
        queryFn: () => fetchQuoteById(id),
        enabled: id != null,
        retry: false,
    });

    useEffect(() => {
        if (id == null) return undefined;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        const handleRefresh = (filterFn) => {
            if (filterFn && !filterFn()) return;
            if (isDirtyRef?.current) {
                pendingRefreshRef.current = true;
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['quote', id] });
        };

        // Only refresh on detail events that target THIS quote — other open
        // detail pages in other tabs would otherwise stomp each other.
        connection.on('UpdateQuoteDetail', (quoteId) => handleRefresh(() => quoteId === id));
        connection.on('UpdateQuoteTable', () => {
            handleRefresh();
            // Also propagate to the list cache. useQuotes is unmounted while the
            // user is on this page (staleTime: 30 s would otherwise keep the list
            // stale when they navigate back after a duplicate / delete / convert).
            queryClient.invalidateQueries({ queryKey: ['quotes'] });
        });

        connection.start().catch(err => console.error('SignalR connection error:', err));

        return () => {
            connection.stop();
        };
    }, [queryClient, id, isDirtyRef]);

    const flushPendingRefresh = () => {
        if (pendingRefreshRef.current) {
            pendingRefreshRef.current = false;
            queryClient.invalidateQueries({ queryKey: ['quote', id] });
        }
    };

    return { ...query, flushPendingRefresh };
}
